import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type TabSource = 'posts' | 'replies' | 'reposts' | 'likes'

/** Per-user adult-content override. 'inherit' (or absent) = use the
 *  global gallery sensitivity. */
export type TabSensitivity = 'inherit' | 'all' | 'safe' | 'adult'

export interface OpenedTab {
  handle: string
  did: string | null
  displayName: string | null
  avatar: string | null
  addedAt: number
  /** ISO of the latest post indexedAt the user has acknowledged for
   *  this tab. The activity-feed compares incoming posts against this
   *  to decide what's "new". Updated on view via markSeen. */
  lastSeenAt?: string | null
  /** Per-user source filter for the gallery. Absent = follow the global
   *  enabledSources pref. */
  enabledSources?: TabSource[]
  /** Per-user sensitivity override for the gallery. */
  sensitivity?: TabSensitivity
  /** Pinned users sort first in the watched list + grouped gallery. */
  pinned?: boolean
}

interface TabsState {
  tabs: OpenedTab[]
  open: (
    tab: { handle: string; did?: string; displayName?: string | null; avatar?: string | null },
  ) => void
  close: (handle: string) => void
  reorder: (handles: string[]) => void
  patch: (handle: string, patch: Partial<OpenedTab>) => void
  /** Mark all activity up to (and including) the given iso timestamp
   *  as seen for that tab. */
  markSeen: (handle: string, iso: string) => void
  /** Toggle one gallery source for one user. `fallback` seeds the
   *  per-user list from the current global prefs on first toggle. */
  toggleSource: (handle: string, source: TabSource, fallback: TabSource[]) => void
  /** Set (or clear, with 'inherit') the per-user sensitivity override. */
  setSensitivity: (handle: string, v: TabSensitivity) => void
  togglePinned: (handle: string) => void
  clear: () => void
}

export const useTabs = create<TabsState>()(
  persist(
    (set) => ({
      tabs: [],
      open: (t) =>
        set((s) => {
          const handle = t.handle.toLowerCase()
          if (s.tabs.some((x) => x.handle === handle)) {
            return {
              tabs: s.tabs.map((x) =>
                x.handle === handle
                  ? {
                      ...x,
                      did: t.did ?? x.did,
                      displayName: t.displayName ?? x.displayName,
                      avatar: t.avatar ?? x.avatar,
                    }
                  : x,
              ),
            }
          }
          return {
            tabs: [
              ...s.tabs,
              {
                handle,
                did: t.did ?? null,
                displayName: t.displayName ?? null,
                avatar: t.avatar ?? null,
                addedAt: Date.now(),
              },
            ],
          }
        }),
      close: (handle) =>
        set((s) => ({ tabs: s.tabs.filter((x) => x.handle !== handle.toLowerCase()) })),
      reorder: (handles) =>
        set((s) => ({
          tabs: handles
            .map((h) => s.tabs.find((x) => x.handle === h.toLowerCase()))
            .filter((x): x is OpenedTab => !!x),
        })),
      patch: (handle, patch) =>
        set((s) => ({
          tabs: s.tabs.map((x) =>
            x.handle === handle.toLowerCase() ? { ...x, ...patch } : x,
          ),
        })),
      toggleSource: (handle, source, fallback) =>
        set((s) => ({
          tabs: s.tabs.map((x) => {
            if (x.handle !== handle.toLowerCase()) return x
            const cur = x.enabledSources ?? fallback
            const next = cur.includes(source)
              ? cur.filter((k) => k !== source)
              : [...cur, source]
            return { ...x, enabledSources: next }
          }),
        })),
      setSensitivity: (handle, v) =>
        set((s) => ({
          tabs: s.tabs.map((x) =>
            x.handle === handle.toLowerCase()
              ? { ...x, sensitivity: v === 'inherit' ? undefined : v }
              : x,
          ),
        })),
      togglePinned: (handle) =>
        set((s) => ({
          tabs: s.tabs.map((x) =>
            x.handle === handle.toLowerCase() ? { ...x, pinned: !x.pinned } : x,
          ),
        })),
      markSeen: (handle, iso) =>
        set((s) => ({
          tabs: s.tabs.map((x) => {
            if (x.handle !== handle.toLowerCase()) return x
            const prev = x.lastSeenAt ?? ''
            // Only advance the watermark — never roll back.
            return iso > prev ? { ...x, lastSeenAt: iso } : x
          }),
        })),
      clear: () => set({ tabs: [] }),
    }),
    { name: 'bskyhq:tabs', version: 1 },
  ),
)
